/**
 * Settings → GitHub access token (self-hosted installs).
 *
 * Shown only when the server has NO GitHub App configured (i.e. local /
 * docker-only installs): a fine-grained personal access token gives
 * private-repo analysis with the same per-repo, read-only model. Stored
 * encrypted server-side; consumed by the clone fallback path.
 */
import { useEffect, useState } from 'react';
import { api } from '../../services/api';

export function GithubTokenSection() {
  const [visible, setVisible] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await api.getGithubAppInfo();
        if (info.configured) return; // SaaS mode — GitHub App handles repo access
        const st = await api.getGithubTokenStatus();
        if (!cancelled) { setConfigured(st.configured); setVisible(true); }
      } catch { /* signed out — hide */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!visible) return null;

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.saveGithubToken(token.trim());
      setMsg(r.message); setConfigured(true); setToken('');
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed to save token'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.deleteGithubToken();
      setMsg(r.message); setConfigured(false);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed to remove token'); }
    finally { setBusy(false); }
  };

  return (
    <section className="border border-border rounded-lg p-6 mt-6">
      <h2 className="text-xl font-semibold mb-1">GitHub Access Token</h2>
      <p className="text-sm text-muted-foreground mb-4">
        This install has no GitHub sign-in. To analyze <span className="font-medium">private</span>{' '}
        repositories, create a{' '}
        <a href="https://github.com/settings/personal-access-tokens/new" target="_blank" rel="noreferrer" className="text-primary hover:underline">
          fine-grained personal access token
        </a>{' '}
        — choose <span className="font-medium">Only select repositories</span> and grant{' '}
        <span className="font-medium">Contents: Read-only</span>. Veodiagram stores it encrypted
        and only ever reads code. Public repos need no token.
      </p>

      {err && <div className="mb-3 rounded-md border border-destructive/20 bg-destructive/10 p-2 text-sm text-destructive">{err}</div>}
      {msg && <div className="mb-3 rounded-md border border-emerald-300/40 bg-emerald-500/10 p-2 text-sm">{msg}</div>}

      {configured ? (
        <div className="flex items-center gap-3">
          <span className="text-sm">A token is saved for your account.</span>
          <button onClick={remove} disabled={busy}
            className="rounded-md border border-destructive/40 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50">
            Remove token
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="github_pat_… or ghp_…"
            className="flex-1 px-3 py-1.5 rounded-md border border-border bg-background text-sm font-mono"
          />
          <button onClick={save} disabled={busy || token.trim().length < 20}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save token'}
          </button>
        </div>
      )}
    </section>
  );
}
